<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  typeIcon as iconFor,
  statusColor as colorFor,
} from 'dashboard/components/bookings/bookingTypes';

const props = defineProps({
  booking: { type: Object, required: true },
  // Only supplied by the account-wide bookings list, where the card is shown
  // outside a client's own context and needs to name the client.
  contact: { type: Object, default: null },
});

const emit = defineEmits(['view', 'openContact']);

const { t } = useI18n();

const typeIcon = computed(() => iconFor(props.booking.booking_type));

const typeLabel = computed(() => {
  const key = props.booking.booking_type?.toUpperCase();
  return t(`CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.TYPE_OPTIONS.${key}`);
});

const statusColor = computed(() => colorFor(props.booking.status));

const ratePlan = computed(() => props.booking.details?.ratePlan || '');

const statusLabel = computed(() => {
  const key = props.booking.status?.toUpperCase();
  return t(`CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.STATUS_OPTIONS.${key}`);
});

const dateRange = computed(() => {
  const start = props.booking.start_date;
  const end = props.booking.end_date;
  if (!start) return '';
  const startStr = new Date(start).toLocaleDateString();
  if (!end) return startStr;
  return `${startStr} — ${new Date(end).toLocaleDateString()}`;
});

const amountDisplay = computed(() => {
  const amount = props.booking.total_amount;
  const currency = props.booking.currency;
  if (!amount) return '';
  return `${currency || 'AED'} ${Number(amount).toLocaleString()}`;
});
</script>

<template>
  <div
    class="border-b border-n-weak px-4 py-3 cursor-pointer hover:bg-n-alpha-1 transition-colors"
    @click="emit('view', booking)"
  >
    <div class="flex items-start gap-3">
      <div
        class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-n-alpha-2 text-n-slate-11"
      >
        <span :class="typeIcon" class="size-4" />
      </div>
      <div class="flex-1 min-w-0">
        <div class="flex items-center justify-between gap-2">
          <h4 class="text-sm font-medium text-n-slate-12 truncate">
            {{ booking.title }}
          </h4>
          <span
            :class="statusColor"
            class="shrink-0 rounded-full px-2 py-0.5 text-xs font-medium"
          >
            {{ statusLabel }}
          </span>
        </div>
        <div
          class="mt-1 flex flex-wrap items-center gap-2 text-xs text-n-slate-11"
        >
          <span>{{ typeLabel }}</span>
          <span
            v-if="ratePlan"
            class="rounded-md bg-n-alpha-2 px-1.5 py-0.5 font-mono text-n-slate-11"
          >
            {{ ratePlan }}
          </span>
          <span v-if="booking.reference_number" class="font-mono">
            #{{ booking.reference_number }}
          </span>
          <button
            v-if="contact"
            type="button"
            class="flex items-center gap-1 hover:underline text-n-slate-12"
            @click.stop="emit('openContact', contact.id)"
          >
            <span class="i-lucide-user size-3" />
            {{ contact.name }}
          </button>
        </div>
        <div
          v-if="dateRange || amountDisplay"
          class="mt-1 flex items-center gap-3 text-xs text-n-slate-11"
        >
          <span v-if="dateRange" class="flex items-center gap-1">
            <span class="i-lucide-calendar size-3" />
            {{ dateRange }}
          </span>
          <span v-if="amountDisplay" class="font-medium text-n-slate-12">
            {{ amountDisplay }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
